import { META, type KaiDB, type LocalHousehold, type MetaRow } from '@/db/db';

function babyMeta(row: MetaRow, babyId: string, eventIds: Set<string>) {
  const value = row.value as {baby_id?: string} | null;
  return row.key.startsWith(`importSource:${babyId}:`) || value?.baby_id === babyId
    || [...eventIds].some(id => row.key === `undoEvent:${id}` || row.key === `importKey:${id}`);
}
// Archives hold the same shape as the visible workspace. Do not drop unrelated drafts.
function scrubArchive(value: unknown, householdId: string, babyId: string): unknown {
  const saved = value as {household?: LocalHousehold; babies?: {id: string}[]; events?: {id: string; baby_id: string}[];
    outbox?: {entity_id: string; data: {baby_id?: string}}[]; imports?: {baby_id: string}[]; predictions?: {baby_id: string}[];
    metadata?: MetaRow[]; activeBabyId?: string};
  if (saved?.household?.id !== householdId) return value;
  const events = new Set((saved.events ?? []).filter(e => e.baby_id === babyId).map(e => e.id));
  return { ...saved, babies: saved.babies?.filter(b => b.id !== babyId), events: saved.events?.filter(e => e.baby_id !== babyId),
    outbox: saved.outbox?.filter(o => o.entity_id !== babyId && o.data?.baby_id !== babyId && !events.has(o.entity_id)),
    imports: saved.imports?.filter(b => b.baby_id !== babyId), predictions: saved.predictions?.filter(p => p.baby_id !== babyId),
    metadata: saved.metadata?.filter(r => !babyMeta(r, babyId, events) && !(r.key === META.activeBabyId && r.value === babyId)
      && !(r.key.startsWith('promotionRequest:') && r.key.endsWith(`:${householdId}`))),
    activeBabyId: saved.activeBabyId === babyId ? undefined : saved.activeBabyId };
}
export async function purgeBabyArchives(store: KaiDB, householdId: string, babyId: string, cloud: boolean) {
  for (const row of await store.meta.toArray()) {
    if ((row.key.startsWith('cloudContext:') && cloud) || (row.key === META.localContext && !cloud)) {
      await store.meta.put({ ...row, value: scrubArchive(row.value, householdId, babyId) });
    }
    if (row.key.startsWith('promotionRequest:') && row.key.endsWith(`:${householdId}`)) await store.meta.delete(row.key);
  }
}
export async function purgeBaby(store: KaiDB, householdId: string, babyId: string, cloud: boolean) {
  const current = await store.babies.get(babyId);
  if (current && current.household_id !== householdId) throw new Error('Invalid retirement household.');
  const events = await store.events.where('baby_id').equals(babyId).toArray();
  const ids = new Set(events.map(e => e.id));
  await store.events.bulkDelete([...ids]); await store.babies.delete(babyId);
  await store.outbox.filter(o => o.household_id === householdId && (o.entity_id === babyId || ids.has(o.entity_id) || ('baby_id' in o.data && o.data.baby_id === babyId))).delete();
  await store.importBatches.where('baby_id').equals(babyId).delete();
  await store.predictionLog.filter(p => p.baby_id === babyId).delete();
  for (const row of await store.meta.toArray()) {
    if (babyMeta(row, babyId, ids) || row.key === META.activeBabyId && row.value === babyId) await store.meta.delete(row.key);
  }
  // Pending snapshot uploads contain full records, so an erased workspace may
  // never reuse one of those requests. The user can explicitly prepare a new one.
  await purgeBabyArchives(store, householdId, babyId, cloud);
}


